<template>
  <v-container fluid class="fill-information-view" pa-0>
    <v-form ref="form">
      <v-row no-gutters row wrap>
        <v-col cols="12" md="6" class="pe-grey pa-5">
          <h2>Kiinteistön perustiedot</h2>
          <v-row row wrap>
            <v-col cols="12" class="pt-4 pb-0">
              <v-select filled background-color="white" label="Valitse rakennuksen tyyppi" :rules="[$rules.required]" :items="buildingTypes" v-model="buildingType"></v-select>
            </v-col>
            <v-col cols="12" class="pt-0 pb-0">
              <v-select aria filled background-color="white" label="Valitse asuinalue"
                        :menu-props="{maxHeight: 500}"
                        :items="areas" v-model="area" :rules="[$rules.required]"></v-select>
            </v-col>
            <v-col cols="12" class="pt-0 pb-2">
              <template v-if="$vuetify.breakpoint.xsOnly">
                <v-select filled background-color="white" label="Valitse rakennusajankohta"
                          :items="years" item-text="text" item-value="value" v-model="buildingYear"></v-select>
              </template>
              <template v-else>
                <v-label for="rakennusajankohta"><span class="caption pl-3">Valitse rakennusajankohta</span></v-label><br>
                <v-btn-toggle id="rakennusajankohta" role="radiogroup" mandatory v-model="buildingYear">
                  <v-btn v-for="year in years" :key="year.value" :value="year.value" :rules="[$rules.required]">{{ year.text }}</v-btn>
                </v-btn-toggle>
              </template>
            </v-col>

            <v-col cols="12" class="pt-4 pb-0">
              <v-text-field filled background-color="white"
                            type="text" inputmode="numeric" pattern="[0-9.,]*"
                            v-if="askDistance"
                            label="Liittymisetäisyys, m"
                            :hint="distanceHint"
                            v-model="distance"
                            persistent-hint
                            :rules="[$rules.required, $rules.number, $rules.realistic]"></v-text-field>
            </v-col>
            <v-col cols="12" class="pt-4 pb-0">
              <v-text-field filled background-color="white"
                            type="text" inputmode="numeric" pattern="[0-9.,]*"
                            label="Rakennuksen pinta-ala, m²"
                            v-model="propertySize"
                            :rules="[$rules.required, $rules.number, $rules.realistic]"></v-text-field>
            </v-col>
            <v-col cols="12" class="pt-0 pb-0">
              <v-text-field filled
                            background-color="white"
                            label="Rakennuksen tilavuus, m³"
                            type="text" inputmode="numeric" pattern="[0-9.,]*"
                            persistent-hint
                            :rules="[$rules.required, $rules.number, $rules.realistic]"
                            hint="Jos tilavuutta ei anneta, niin käytetään perushuonekorkeutta (asuintalossa 2,5m tai hallissa 4,0m) tilavuuden laskemiseen."
                            v-model="propertyVolume"></v-text-field>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" class="pe-grey pa-5">
          <h2>Nykyiset lämmitystiedot</h2>
          <v-row row wrap>
            <v-col cols="12" class="pt-4 pb-0">
              <v-select filled background-color="white"
                        label="Nykyinen lämmitysmuoto" :items="heatingMethods" v-model="currentHeating" :rules="[$rules.required]"></v-select>
            </v-col>
            <v-col cols="12" class="pt-0 pb-0">
              <v-text-field filled
                            :disabled="currentHeating === 'uudiskohde' || currentHeating === null"
                            background-color="white"
                            :hint="currentConsumptionHint"
                            persistent-hint
                            type="text" inputmode="numeric" pattern="[0-9.,]*"
                            :rules="[$rules.number, $rules.realistic]"
                            :label="currentConsumptionLabel"
                            v-model="currentConsumption"></v-text-field>
            </v-col>
            <v-col cols="12" class="pt-0 pb-0">
              <v-text-field filled
                            :disabled="buildingType === 'okt'"
                            background-color="white"
                            type="text" inputmode="numeric" pattern="[0-9.,]*"
                            label="Kiinteistön tehon tarve (valinnainen), kW"
                            persistent-hint
                            hint="Jos tehon tarve ei ole tiedossa, niin se lasketaan kiinteistön muiden tietojen perusteella."
                            v-model="propertyPowerDemand"></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row row wrap no-gutters>
        <v-col cols="12"
               @click="calculatePrice"
               class="pe-green pa-5"
               :class="priceCanBeCalculated ? '' : 'green--text text--darken-2'">
          <v-row align="center" justify="center">
            <button title="Laske hinta" type="button" class="main-btn">Laske hinta <v-icon style="margin-top: -4px;" size="26" :color="priceCanBeCalculated ? '' : 'green darken-2'">mdi-arrow-right</v-icon></button>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
  import { multiply } from 'mathjs';
  import laskuri from "../laskuri";
  import params from "../params";

  export default {
    name: "PropertyInformationForm",
    data: () => ({ // Oletukset storesta
      distance: null,
      buildingTypes: params.buildingTypes,
      buildingType: null,
      areas: params.areas,
      area: null,
      propertySize: null,
      propertyVolume: null,
      years: params.years,
      buildingYear: "2",
      heatingMethods: params.heatingMethods,
      currentHeating: 'uudiskohde',
      propertyPowerDemand: null,
      currentConsumption: null,
    }),
    methods: {
      calculateVolume(val) {
        if (!val && this.propertySize === null) {
          return;
        }
        const propertySize = val ? val : this.propertySize;
        const tallRooms = ['teol', 'halli'];
        const isTallRoom = tallRooms.indexOf(this.buildingType) !== -1;
        const roomHeight = isTallRoom ? 4 : 2.5;
        this.propertyVolume = multiply(propertySize, roomHeight);
      },
      calculatePrice() {
        if (!this.$refs.form.validate()) return;

        const arvot = laskuri.laskeKaikki(this.area, this.buildingType, this.teho, this.energiaKulutus, this.currentHeating, this.currentConsumption, this.distance);
        const event = {
          prices: arvot,
          currentHeating: this.currentHeating,
        }
        this.$sendAnalyticsEvent('price-calculated');
        this.$calculationDone();
        this.$emit('price-calculated', event);
      },
    },
    computed: {
      askDistance() {
        return this.area && (this.area.startsWith('pori_') || this.area === "muu");
      },
      distanceHint() {
        if (!this.askDistance) {
          return ''
        }

        if (this.buildingType === 'okt') return "Liittymismaksu on 135 € x liittymisetäisyys + 430 €. Tarkista liittymääsi varten tarvittava rakentamisen määrä myyjältäsi.";
        else return "Liittymismaksu on 240 € x liittymisetäisyys.";
      },
      currentConsumptionLabel() {
        return 'Nykykulutus';
      },
      currentConsumptionHint() {
        if (this.currentHeating === 'uudiskohde') {
          return 'Ei käytössä uudiskohteella';
        } else if (this.currentHeating === 'oljy') {
          return 'Nykyinen kulutus, öljylitraa vuodessa';
        } else if (this.currentHeating === 'sahko') {
          return 'Nykyinen kokonaissähkönkulutus, kWh/vuosi';
        } else {
          return 'Nykyinen lämmityksen kulutus, kWh/vuosi';
        }
      },
      teho() {
        if (this.propertyPowerDemand != null && this.propertyPowerDemand.length > 0) {
          const asNumber = Number.parseFloat(this.propertyPowerDemand);
          if (asNumber > 0) {
            return asNumber;
          }
        }

        return laskuri.laskeTehonTarve(this.buildingType, this.buildingYear, this.propertyVolume);
      },
      energiaKulutus() {
        return laskuri.laskeOminaiskulutus(this.buildingType, this.buildingYear, this.propertyVolume);
      },
      priceCanBeCalculated() {
        if (this.buildingType == null) return false;
        if (this.area == null) return false;
        if (this.propertySize == null) return false;
        if (this.propertyVolume == null) return false;
        if (this.buildingYear == null) return false;
        if (this.askDistance && this.distance == null) return false;
        if (this.currentHeating == null) {
          if (this.propertyPowerDemand == null) return false;
        }
        return true;
      },
    },
    mounted() {
      this.area = this.$store.state.area;
      this.propertyVolume = this.$store.state.propertyVolume;
      this.buildingYear = this.$store.state.buildingYear;
      this.currentHeating = this.$store.state.currentHeating;
      this.currentConsumption = this.$store.state.currentConsumption;
      this.propertyPowerDemand = this.$store.state.propertyPowerDemand;
      this.propertySize = this.$store.state.propertySize;
      this.buildingType = this.$store.state.buildingType;
      this.distance = this.$store.state.distance;
    },
    watch: {
      askDistance(newValue) {
        if (!newValue) {
          this.distance = null;
        } else {
          this.distance = 30;
        }
      },
      distance(newValue) {
        this.$store.commit('setDistance', newValue);
      },
      area(newValue) {
        this.$store.commit('setArea', newValue);
      },
      propertyVolume(newValue) {
        this.$store.commit('setPropertyVolume', newValue);
      },
      buildingYear(newValue) {
        this.$store.commit('setBuildingYear', newValue);
      },
      currentHeating(newValue) {
        this.$store.commit('setCurrentHeating', newValue);
        this.currentConsumption = null;
        if (newValue === 'uudiskohde') {
          this.buildingYear = '2';
        }
      },
      currentConsumption(newValue) {
        this.$store.commit('setCurrentConsumption', newValue);
      },
      propertyPowerDemand(newValue) {
        this.$store.commit('setPropertyPowerDemand', newValue);
      },
      propertySize(val) {
        if (val) {
          this.calculateVolume(val);
        }

        this.$store.commit('setPropertySize', val);
      },
      buildingType(to, from) {
        this.calculateVolume();
        if (to === 'okt') {
          this.propertyPowerDemand = '10';
        }

        if (from === 'okt') {
          this.propertyPowerDemand = null;
        }
        this.$store.commit('setBuildingType', to);
      },
    },
  }
</script>


<style lang="scss">
  .fill-information-view {
    .v-input {
      border-radius: 3px;
    }

    .v-input__slot {
      box-shadow: none !important;

      &.white {
        border: 1px solid rgba(0,0,0,0.35) !important;
      }

      &::before {
        display: none;
      }
    }
  }
</style>
